import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, Send, CheckCircle2 } from 'lucide-react';
import { authForgotPassword } from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await authForgotPassword(email.trim());
      setSent(true);
    } catch {
      // Backend always returns 200 to prevent enumeration; still guard.
      setSent(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#e2ede6] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-6">
          <span className="text-[#0f5c3d] leading-none" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '52px', fontWeight: 500 }}>E</span>
        </Link>
        <div className="bg-white border border-black/5 rounded-2xl shadow-sm p-6 lg:p-8" data-testid="forgot-password-page">
          <h1 className="text-3xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Forgot password?
          </h1>
          <p className="mt-1 text-sm text-[#0b1a2e]/60">
            Enter your email and we'll send you a reset link. Links expire in 1 hour.
          </p>

          {sent ? (
            <div className="mt-6 bg-[#f7fbf8] border border-emerald-100 rounded-lg p-5 flex gap-3 items-start" data-testid="forgot-password-sent">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-[#0b1a2e]">Check your inbox</div>
                <p className="text-xs text-[#0b1a2e]/70 mt-1 leading-relaxed">
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                  Don't see it in a few minutes? Check spam, or try again.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Email</span>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                  <input
                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email" data-testid="forgot-email"
                    className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                    placeholder="you@emeraldcorporates.com"
                  />
                </div>
              </label>
              <button
                type="submit" disabled={loading} data-testid="submit-forgot"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f5c3d] text-white font-medium text-sm hover:bg-[#0b1a2e] transition disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send reset link
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-[#0b1a2e]/50">
          <Link to="/login" className="hover:text-[#0f5c3d]">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
